import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowRight, Ship, Package, ShieldCheck } from 'lucide-react';
import { subscribeToPushNotifications } from '@/utils/pushNotifications';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Login = ({ setUser }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { token, user } = response.data;

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);

      subscribeToPushNotifications(token).then(result => {
        if (result.success && process.env.NODE_ENV === 'development') console.log('Push notifications enabled');
      });

      toast.success('Login successful!');
      navigate(user.role === 'admin' ? '/admin' : '/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 auth-bg" data-testid="login-page">
      {/* Brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between overflow-hidden p-12 border-r border-[#1E293B]">
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage: "linear-gradient(160deg, rgba(6,10,20,0.75), rgba(12,20,39,0.65)), url('https://images.unsplash.com/photo-1585713181935-d5f622cc2415?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwxfHxjYXJnbyUyMHNoaXAlMjBvY2VhbiUyMGFlcmlhbHxlbnwwfHx8fDE3OTE2MDA0ODZ8MA&ixlib=rb-4.1.0&q=85')",
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="relative z-10 flex items-center gap-3">
          <div className="bg-white/95 rounded-xl p-2">
            <img src="/dac-logo.png" alt="DAC Shipping" className="h-10 w-10 object-contain" />
          </div>
          <span className="font-black text-2xl tracking-tight text-white">DAC SHIPPING</span>
        </div>

        <div className="relative z-10 max-w-md">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#FF6B35] mb-4">USA → Guyana</p>
          <h2 className="text-4xl font-black tracking-tight text-white leading-tight mb-5">
            Your packages, shipped home.
          </h2>
          <p className="text-slate-300 text-base leading-relaxed">
            Track shipments, grab your US address, and manage invoices — all in one place.
          </p>
          <div className="mt-8 flex flex-col gap-3 text-slate-200 text-sm">
            <span className="flex items-center gap-3"><Package className="h-4 w-4 text-[#38BDF8]" /> Real-time package tracking</span>
            <span className="flex items-center gap-3"><Ship className="h-4 w-4 text-[#38BDF8]" /> Fast ocean freight to Guyana</span>
            <span className="flex items-center gap-3"><ShieldCheck className="h-4 w-4 text-[#38BDF8]" /> Secure, trusted & reliable</span>
          </div>
        </div>
        <div className="relative z-10 text-xs text-slate-400">WhatsApp: +1 592 635 0592 · Instagram @DAC_Shipping</div>
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center px-5 py-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex justify-center mb-8">
            <div className="bg-white/95 rounded-xl p-3">
              <img src="/dac-logo.png" alt="DAC Shipping" className="h-14 w-auto object-contain" />
            </div>
          </div>

          <h1 className="text-4xl font-black tracking-tight text-white">Welcome back</h1>
          <p className="text-slate-400 mt-2 mb-8">Sign in to your DAC SHIPPING account</p>

          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-slate-300">Email</Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 h-12"
                required
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-300">Password</Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 h-12"
                required
              />
              <div className="text-right mt-2">
                <Link to="/forgot-password" className="text-sm text-[#FF6B35] hover:text-[#FF8557] font-medium">
                  Forgot password?
                </Link>
              </div>
            </div>
            <Button
              data-testid="login-submit-btn"
              type="submit"
              className="w-full h-12 bg-[#FF6B35] hover:bg-[#FF8557] text-white font-semibold text-base rounded-lg transition-colors"
              disabled={loading}
            >
              {loading ? 'Signing in...' : <span className="flex items-center justify-center gap-2">Sign In <ArrowRight className="h-4 w-4" /></span>}
            </Button>
          </form>

          <div className="mt-8 text-center text-sm">
            <span className="text-slate-400">Don't have an account? </span>
            <Link to="/signup" className="text-[#FF6B35] hover:text-[#FF8557] font-semibold">
              Sign up
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
